import { Box, TextField, Autocomplete, Button, Typography } from '@mui/material'
import { useState, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { COLORS } from '~theme/colors'
import {
  MultiItemAutocomplete,
  type OnChangeHandlerT,
} from '~components/autocomplete/multi-item-autocomplete'
import {
  useGetPortInfoFilters,
  type FilterField,
} from '~hooks/queries/port-info/use-get-port-info-filters'

const FILTER_HIERARCHY = [
  'portCountry',
  'portName',
  'terminalName',
  'berthName',
] as const

type FilterKey = (typeof FILTER_HIERARCHY)[number]

const lastVisitOptions = [
  { label: 'Last visit: any time', value: '' },
  { label: 'Last visit: within 7 days', value: '7' },
  { label: 'Last visit: within 30 days', value: '30' },
  { label: 'Last visit: within 90 days', value: '90' },
  { label: 'Last visit: within 180 days', value: '180' },
]

const defaultLastVisit = lastVisitOptions[0]

export function FilterBar() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeFilter, setActiveFilter] = useState<FilterField | null>(null)

  const selectedCountries = searchParams.getAll('portCountry')
  const selectedPorts = searchParams.getAll('portName')
  const selectedTerminals = searchParams.getAll('terminalName')
  const selectedBerths = searchParams.getAll('berthName')

  const lastVisitParam = searchParams.get('lastVisit')
  const selectedLastVisit =
    lastVisitOptions.find((opt) => opt.value === lastVisitParam) ??
    defaultLastVisit

  const { data: countryData } = useGetPortInfoFilters({
    field: 'portCountry',
    enabled: activeFilter === 'portCountry',
  })

  const { data: portData } = useGetPortInfoFilters({
    field: 'portName',
    portCountry: selectedCountries.length > 0 ? selectedCountries : undefined,
    enabled: activeFilter === 'portName',
  })

  const { data: terminalData } = useGetPortInfoFilters({
    field: 'terminalName',
    portCountry: selectedCountries.length > 0 ? selectedCountries : undefined,
    portName: selectedPorts.length > 0 ? selectedPorts : undefined,
    enabled: activeFilter === 'terminalName',
  })

  const { data: berthData } = useGetPortInfoFilters({
    field: 'berthName',
    portCountry: selectedCountries.length > 0 ? selectedCountries : undefined,
    portName: selectedPorts.length > 0 ? selectedPorts : undefined,
    terminalName: selectedTerminals.length > 0 ? selectedTerminals : undefined,
    enabled: activeFilter === 'berthName',
  })

  const handleLastVisitChange = (
    _: unknown,
    option: (typeof lastVisitOptions)[number] | null,
  ) => {
    setSearchParams((params) => {
      if (option === null) {
        params.delete('lastVisit')
      } else {
        params.set('lastVisit', option.value)
      }
      return params
    })
  }

  const createFilterHandler = useCallback(
    (filterKey: FilterKey): OnChangeHandlerT => {
      const filterIndex = FILTER_HIERARCHY.indexOf(filterKey)
      const higherLevelKeys = FILTER_HIERARCHY.slice(0, filterIndex)
      const keysToReset = FILTER_HIERARCHY.slice(filterIndex)

      return (_, values) => {
        setSearchParams((params) => {
          keysToReset.forEach((key) => {
            params.delete(key)
          })

          if (values.length > 0) {
            params.set('lastVisit', '')
            values.forEach((v) => {
              params.append(filterKey, v)
            })
          } else {
            const hasHigherFilters = higherLevelKeys.some(
              (key) => params.getAll(key).length > 0,
            )
            if (!hasHigherFilters) {
              params.delete('lastVisit')
            }
          }
          return params
        })
      }
    },
    [setSearchParams],
  )

  const handleCountryChange = createFilterHandler('portCountry')
  const handleTerminalChange = createFilterHandler('terminalName')
  const handleBerthChange = createFilterHandler('berthName')

  const handlePortChange: OnChangeHandlerT = (
    e,
    values,
    reason,
    details,
    helperInput,
  ) => {
    const portNames = values.map((v) =>
      typeof v === 'string' ? v : v.portName,
    )
    createFilterHandler('portName')(e, portNames, reason, details, helperInput)
  }

  const handleResetFilters = () => {
    setSearchParams((params) => {
      params.delete('lastVisit')
      FILTER_HIERARCHY.forEach((key) => {
        params.delete(key)
      })
      return params
    })
  }

  return (
    <Box
      sx={{
        bgcolor: COLORS.primary.white,
        borderRadius: 1,
        border: 1,
        borderColor: COLORS.greys.light,
      }}
    >
      <Box
        className="tour-step-port-info-filter-bar"
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          p: 2,
        }}
      >
        <Autocomplete
          className="tour-step-port-info-last-visit-filter"
          size="small"
          options={lastVisitOptions}
          value={selectedLastVisit}
          onChange={handleLastVisitChange}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          disableClearable
          renderInput={(params) => <TextField {...params} />}
          sx={{ flex: '1 1 200px' }}
        />

        <Box
          className="tour-step-port-info-location-filters"
          onFocus={() => setActiveFilter('portCountry')}
          sx={{ flex: '1 1 150px' }}
        >
          <MultiItemAutocomplete
            size="small"
            label="All countries"
            input={selectedCountries}
            options={countryData?.portCountry ?? []}
            onChangeHandler={handleCountryChange}
          />
        </Box>

        <Box
          onFocus={() => setActiveFilter('portName')}
          sx={{ flex: '1 1 150px' }}
        >
          <MultiItemAutocomplete
            size="small"
            label="All ports"
            input={selectedPorts}
            options={portData?.portName ?? []}
            onChangeHandler={handlePortChange}
            getOptionLabel={(
              option: string | { portName: string; portCode: string },
            ) => {
              if (typeof option === 'string') return option
              return option.portCode
                ? `${option.portName} (${option.portCode})`
                : option.portName
            }}
            isOptionEqualToValue={(
              option: string | { portName: string; portCode: string },
              value: string,
            ) => {
              const optionName =
                typeof option === 'string' ? option : option.portName
              return optionName === value
            }}
          />
        </Box>

        <Box
          onFocus={() => setActiveFilter('terminalName')}
          sx={{ flex: '1 1 150px' }}
        >
          <MultiItemAutocomplete
            size="small"
            label="All terminals"
            input={selectedTerminals}
            options={terminalData?.terminalName ?? []}
            onChangeHandler={handleTerminalChange}
          />
        </Box>

        <Box
          onFocus={() => setActiveFilter('berthName')}
          sx={{ flex: '1 1 150px' }}
        >
          <MultiItemAutocomplete
            size="small"
            label="All berths"
            input={selectedBerths}
            options={berthData?.berthName ?? []}
            onChangeHandler={handleBerthChange}
          />
        </Box>
        <Button
          className="tour-step-port-info-reset-filters"
          onClick={handleResetFilters}
        >
          Reset Filters
        </Button>
      </Box>
      <Typography
        variant="caption"
        sx={{ px: 2, pb: 1.5, color: COLORS.greys.dark, display: 'block' }}
      >
        Filters apply to "Ports" section only
      </Typography>
    </Box>
  )
}
